import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // Import close icon
import { getSampleCollections } from "../../services/labService"; // Import the service to fetch samples
import { getAllLabTests } from "../../services/labtestService"; // Import the service to fetch lab tests from base table
import { updateReportStatusCollect } from "../../services/pathLabTestReportService"
import { resolvePrescriptionReports } from "./labTestWorkflowUtils";
import { updatePatientPathologyCollect } from "../../services/PatientPathologyService"; // Import the new service
import dayjs from "dayjs";
import { getOpdLabTestStatus, filterTestsBySelectedSamples, extractLabTestSampleIds, findCatalogLabTest } from "./labTestWorkflowUtils";
import FormModalActions from "../../components/FormModalActions";
import { STANDARD_MODAL_SURFACE_SX } from "../../utils/formModalStyles";
import { recordSamplesCollected } from "../../services/sampleTransactionService";
import { buildSampleCollectedId } from "../../utils/sampleTransactionUtils";
import {
  usePathBomCollectInventoryFlow,
  buildSampleCollectSuccessMessage,
} from "../../hooks/usePathBomCollectInventoryFlow";

const CollectSampleModal = ({ open, onClose, test, onSampleCollected, onResultAwaited, onProcessingChange }) => {
  const [samples, setSamples] = useState([]);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [samplesCollected, setSamplesCollected] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [collectedSampleIds, setCollectedSampleIds] = useState([]);
  const { inventoryDialogs, gateReviewAndDeduct } = usePathBomCollectInventoryFlow();

  const sampleIds = samples.map((sample) => sample.id);
  const allSamplesSelected = sampleIds.length > 0 && sampleIds.every((id) => selectedSamples.includes(id));
  const someSamplesSelected = selectedSamples.length > 0 && !allSamplesSelected;

  const handleSelectAllSamples = (checked) => {
    setSelectedSamples(checked ? sampleIds : []);
  };

  useEffect(() => {
  const fetchSamples = async () => {
    try {
      // Check if patientLabTests is defined and has at least one entry
      if (!test || !test.patientLabTests || test.patientLabTests.length === 0) {
        console.error("No patient lab tests available for the selected test.");
        return; // Exit if no lab tests are available
      }

      // Step 1: Fetch all lab tests from base table (not organization-specific)
      const allLabTests = await getAllLabTests();
      
      // Step 2: Filter patientLabTests to only include those with resultStatus "BILLED"
      const billedTests = (test.patientLabTests || []).filter(labTest => String(labTest.resultStatus || "").toUpperCase() === "BILLED");
      
      // Step 3: Build the set of billed test names (no top-level testName on test object)
      const testNamesToMatch = billedTests
        .map(labTest => String(labTest.testName || "").toLowerCase())
        .filter(Boolean);
      
      // Step 4: Filter lab tests to find the ones that match the test names
      const matchingTests = allLabTests.filter(labTest => 
        testNamesToMatch.includes(String(labTest.name || "").toLowerCase())
      );

      if (matchingTests.length > 0) {
        // Step 5: Extract sampleIds from the matching tests (support sampleEntries and sampleId)
        const sampleIds = Array.from(new Set(matchingTests.flatMap(matchingTest => {
          const ids = [];
          if (Array.isArray(matchingTest.sampleEntries) && matchingTest.sampleEntries.length) {
            for (const se of matchingTest.sampleEntries) {
              const v = se?.sample_id ?? se?.sampleId;
              if (v != null && v !== "") ids.push(Number(v));
            }
          }
          if (matchingTest.sampleId) {
            const parts = String(matchingTest.sampleId).split(",").map(s=>s.trim()).filter(Boolean).map(n=>Number(n));
            ids.push(...parts);
          }
          return ids;
        }).filter((n) => !Number.isNaN(n))));

        // Step 6: Fetch all sample collections
        const allSamples = await getSampleCollections();
        
        // Filter samples based on the extracted sampleIds
        const filteredSamples = allSamples.filter(sample => sampleIds.includes(sample.id));
        const samplesWithTests = filteredSamples.map((sample) => {
          const associatedTestNames = billedTests
            .filter((patientTest) =>
              extractLabTestSampleIds(findCatalogLabTest(patientTest, matchingTests)).includes(
                Number(sample.id)
              )
            )
            .map((patientTest) => patientTest.testName)
            .filter(Boolean);
          return { ...sample, associatedTestNames };
        });
        setSamples(samplesWithTests);
      } else {
        console.log("No matching test found for the given test name.");
      }
    } catch (error) {
      console.error("Error fetching samples:", error);
    }
  };

  if (open && test) {
    // Reset selection state whenever opening for a new test
    setSelectedSamples([]);
    setSamplesCollected(false);
    setCollecting(false);
    setCollectedSampleIds([]);
    fetchSamples();
  }
}, [open, test]);

//   const handleCollect = async () => {
//     if (selectedSamples.length === 0) {
//       alert("Please select at least one sample to collect.");
//       return; // Prevent further processing if no samples are selected
//     }
    
//     try {
//         if (test && test.reportId) {
//             const response = await updateReportStatus(test.reportId, "PENDING", null); // Use the reportId from the test object
//             alert("Sample collected successfully!");
//             onSampleCollected(); // Notify parent component

//             // Update the patient_pathology table
//             await updatePatientPathology(test.patientId, test.patientLabTests[0]?.prescriptionId, response.testId, "PENDING", null);
//         } else {
//             console.error("Report ID is undefined in CollectSampleModal:", test);
//         }
//     } catch (error) {
//         console.error("Error updating report status:", error);
//     }
//     onClose();
// };

const handleCollect = async () => {
    if (selectedSamples.length === 0) {
      alert("Please select at least one sample to collect.");
      return; // Prevent further processing if no samples are selected
    }
    onProcessingChange && onProcessingChange(true);
    setCollecting(true);
    try {
      const allLabTests = await getAllLabTests();
      const billedEligible = (test.patientLabTests || []).filter(
        (t) => String(t.resultStatus || "").toUpperCase() === "BILLED"
      );
      const billedTests = filterTestsBySelectedSamples(
        billedEligible,
        allLabTests,
        selectedSamples
      );

      if (billedTests.length === 0) {
        alert("Selected sample is not associated with a billed lab test. Select the sample(s) for the test you want to collect.");
        return;
      }

      // 1) Update only reports for tests linked to the selected sample(s)
      const reports = await resolvePrescriptionReports(test);
      const normalize = (s) => String(s || '').trim().toLowerCase();
      const billedNames = billedTests.map((t) => normalize(t.testName));
      let candidateReports = reports.filter((r) => billedNames.includes(normalize(r.testName)));

      // Prefer same prescriptionId if available
      // Also handle cases where prescriptionId might be null (tests created from patient dashboard)
      const prescId = test?.id;
      if (prescId) {
        const samePresc = candidateReports.filter((r) => r.prescriptionId === prescId || r.prescriptionId === null);
        if (samePresc.length) candidateReports = samePresc;
      }

      // Constrain to same day as prescription creation if date info available
      const prescDate = test?.createdAt ? dayjs(test.createdAt) : null;
      if (prescDate) {
        candidateReports = candidateReports.filter((r) => {
          const rd = r.reportDate || r.createdAt || r.date;
          return rd ? dayjs(rd).isSame(prescDate, 'day') : true;
        });
      }

      // For each test name, pick the latest report within that day
      const byName = new Map();
      for (const r of candidateReports) {
        const key = normalize(r.testName);
        const current = byName.get(key);
        const rDate = r.reportDate || r.createdAt || r.date;
        const rMoment = rDate ? dayjs(rDate) : null;
        if (!current) {
          byName.set(key, r);
        } else {
          const cDate = current.reportDate || current.createdAt || current.date;
          const cMoment = cDate ? dayjs(cDate) : null;
          if (rMoment && cMoment && rMoment.isAfter(cMoment)) byName.set(key, r);
        }
      }
      const chosenReports = Array.from(byName.values());
      // Map test name -> labTestId for downstream patient_pathology updates
      const nameToLabTestId = new Map();
      for (const r of chosenReports) {
        const key = normalize(r.testName);
        const ltId = r.labTestId ?? r.testId;
        if (key && ltId) nameToLabTestId.set(key, ltId);
      }

      // Resolve catalog labTestIds for BOM stock check / deduct (before marking COLLECTED)
      const testsForInventory = [];
      const patientContextByLabTestId = {};
      for (const t of billedTests) {
        const nameKey = normalize(t.testName);
        let labTestId = nameToLabTestId.get(nameKey) || t.labTestId || t.testId;
        if (!labTestId && t.testName) {
          const matchingTest = (allLabTests || []).find((lt) => {
            const ltName = normalize(
              lt.testName || lt.name || lt.test_name || lt.labTestName
            );
            return ltName === nameKey && nameKey !== "";
          });
          if (matchingTest?.id) labTestId = matchingTest.id;
        }
        const numericId = Number(labTestId);
        if (!Number.isFinite(numericId) || numericId <= 0) continue;
        const report = chosenReports.find((r) => normalize(r.testName) === nameKey);
        testsForInventory.push({
          ...t,
          labTestId: numericId,
          testId: numericId,
          testName: t.testName,
        });
        patientContextByLabTestId[numericId] = {
          labTestName: t.testName,
          patientId: test.patientId,
          patientName: test.patientName,
          patientType: "opd",
          doctorName: test.doctorName,
          doctorId: test.doctorId,
          prescriptionId: prescId || t.prescriptionId,
          invoiceId:
            test.invoiceId ??
            test.invoice_id ??
            report?.invoiceId ??
            report?.invoice_id,
          reportId: report?.reportId ?? report?.id ?? t.reportId,
        };
      }

      const inventoryGate = await gateReviewAndDeduct({
        patientLabTests: testsForInventory,
        patientContextByLabTestId,
        catalogLabTests: allLabTests,
      });
      if (!inventoryGate.ok) {
        if (inventoryGate.reason === "cancelled") return;
        if (inventoryGate.reason !== "insufficient_stock") {
          alert(
            inventoryGate.errorMessage ||
              "Failed to deduct pathology inventory. Sample was not collected."
          );
        }
        return;
      }

      for (const r of chosenReports) {
        try {
          await updateReportStatusCollect(r.reportId);
        } catch (e) {
          console.warn('Failed to update report to COLLECTED', r.reportId, e);
        }
      }

      // 2) Update patient_pathology only for tests linked to the selected sample(s)
      const prescIdForUpdate = test?.id;

      for (const t of billedTests) {
        const prescriptionId = prescIdForUpdate || t.prescriptionId; // prefer current card's prescription id
        const nameKey = normalize(t.testName);

        // Try to get labTestId from reports map first, then from test object
        let labTestId = nameToLabTestId.get(nameKey) || t.labTestId || t.testId;

        // Fallback: if labTestId is still missing, look it up by testName from lab tests master list (base table)
        if (!labTestId && t.testName) {
          try {
            const matchingTest = (allLabTests || []).find((lt) => {
              const ltName = normalize(
                lt.testName || lt.name || lt.test_name || lt.labTestName
              );
              return ltName === nameKey && nameKey !== '';
            });
            if (matchingTest && matchingTest.id) {
              labTestId = matchingTest.id;
              console.log(
                'Resolved labTestId from lab tests master list for nameKey',
                nameKey,
                '->',
                labTestId
              );
            }
          } catch (lookupError) {
            console.warn(
              'Could not lookup labTestId for patient_pathology COLLECTED update:',
              lookupError
            );
          }
        }

        if (prescriptionId && labTestId) {
          try {
            console.log('Calling /patient_pathology/collected with:', {
              patientId: test.patientId,
              prescriptionId,
              labTestId,
            });
            await updatePatientPathologyCollect(test.patientId, prescriptionId, labTestId);
          } catch (e) {
            console.warn(
              'Failed to update patient pathology to COLLECTED',
              { prescriptionId, labTestId },
              e
            );
          }
        } else {
          console.warn(
            'Skipping patient_pathology COLLECTED update due to missing ids',
            { prescriptionId, labTestId, nameKey }
          );
        }
      }

      const matchingReport = (name) => {
        const key = normalize(name);
        return chosenReports.find((r) => normalize(r.testName) === key) || null;
      };
      const sampleRows = billedTests
        .map((t) => {
          const prescriptionId = prescIdForUpdate || t.prescriptionId;
          const nameKey = normalize(t.testName);
          const labTestId = nameToLabTestId.get(nameKey) || t.labTestId || t.testId;
          const report = matchingReport(t.testName);
          if (!test.patientId || !labTestId) return null;
          return {
            prescriptionId,
            patientId: test.patientId,
            patientName: test.patientName,
            labTestId,
            labTestName: t.testName,
            billed: true,
            invoiceId: test.invoiceId ?? test.invoice_id ?? report?.invoiceId ?? report?.invoice_id,
            reportId: report?.reportId ?? report?.id ?? t.reportId,
            patientType: "opd",
            sampleCollected: true,
            result: false,
          };
        })
        .filter(Boolean);

      const { uniqueSampleCollectedIds } = await recordSamplesCollected(sampleRows);
      const displayIds =
        uniqueSampleCollectedIds.length > 0
          ? uniqueSampleCollectedIds
          : sampleRows.map((row) => buildSampleCollectedId(row));
      setCollectedSampleIds(displayIds);
      alert(
        buildSampleCollectSuccessMessage({
          deducted: Boolean(inventoryGate.deducted),
          testNames: billedTests.map((t) => t.testName),
          sampleIds: displayIds,
        })
      );
      setSamplesCollected(true);
      onSampleCollected && onSampleCollected();
    } catch (err) {
      console.error('Failed to update status to COLLECTED:', err);
      alert('Failed to update sample collection status.');
    } finally {
      setCollecting(false);
      onProcessingChange && onProcessingChange(false);
    }
  };

  const handleResultAwaited = () => {
    // Reset selected samples and mark samples as not collected
    setSelectedSamples([]); // Clear the selected samples
    setSamplesCollected(false); // Show the Collect Sample button again
    onResultAwaited(); // Notify parent component to open the result modal
    handleModalClose(); // Close the sample collection modal
  };

  const handleModalClose = () => {
    // Clear state on close to avoid leaking selections to the next open
    setSelectedSamples([]);
    setSamplesCollected(false);
    setCollecting(false);
    setCollectedSampleIds([]);
    setSamples([]);
    if (onClose) onClose();
  };

  return (
    <>
    {inventoryDialogs}
    <Modal open={open} onClose={handleModalClose}>
      <Box
        sx={{
          ...STANDARD_MODAL_SURFACE_SX,
          width: 400,
          p: 4,
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={"bold"} sx={{ flexGrow: 1, textAlign: 'center'}}>Collect Samples</Typography>
          <IconButton onClick={handleModalClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 0, mb: 1 }} />
        {/* Details */}
        {test && (
          <Box sx={{ mb: 1, p: 1.25, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Patient Name:</strong> {test.patientName || '—'}</Typography>
            <Typography variant="body2"><strong>Doctor:</strong> {test.doctorName || '—'}</Typography>
            <Typography variant="body2"><strong>Prescription Date:</strong> {test.createdAt ? dayjs(test.createdAt).format('D MMM YYYY') : '—'}</Typography>
            <Typography variant="body2"><strong>Test:</strong> {(test.patientLabTests||[]).filter((t) => getOpdLabTestStatus(t) === "BILLED").map(t=>t.testName).join(', ') || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              Only tests linked to the sample(s) you select will be marked COLLECTED.
            </Typography>
            {collectedSampleIds.length > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Sample ID:</strong> {collectedSampleIds.join(", ")}
              </Typography>
            )}
          </Box>
        )}
        <Grid container spacing={1}> {/* Reduced spacing between items */}
        {samples.length > 0 && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSamplesSelected}
                  indeterminate={someSamplesSelected}
                  onChange={(event) => handleSelectAllSamples(event.target.checked)}
                  disabled={collecting}
                />
              }
              label="Select All"
            />
          </Grid>
        )}
        {samples.length > 0 ? (
          samples.map(sample => (
            <Grid item xs={12} key={sample.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedSamples.includes(sample.id)}
                    onChange={() => {
                      setSelectedSamples(prev =>
                        prev.includes(sample.id)
                          ? prev.filter(id => id !== sample.id)
                          : [...prev, sample.id]
                      );
                    }}
                    disabled={collecting}
                  />
                }
                label={`${sample.name} (${sample.unitType}) - Required: ${sample.requiredQuantity}${
                  Array.isArray(sample.associatedTestNames) && sample.associatedTestNames.length
                    ? ` — ${sample.associatedTestNames.join(", ")}`
                    : ""
                }`}
              />
            </Grid>
          ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Samples are not available for this test.
              </Typography>
            </Grid>
          )}
        </Grid>
        <Divider sx={{ mt: 1, mb: 1 }} />
        <Box display="flex" justifyContent="center" marginTop={2}>
          {!samplesCollected ? (
            <FormModalActions
              onCancel={handleModalClose}
              submitLabel={collecting ? "Processing..." : "Collect"}
              submitType="button"
              onSubmit={handleCollect}
              submitDisabled={collecting || selectedSamples.length === 0}
              loading={collecting}
            />
          ) : (
            <FormModalActions
              onCancel={handleModalClose}
              submitLabel="Result Awaited"
              submitType="button"
              onSubmit={handleResultAwaited}
            />
          )}
        </Box>
      </Box>
    </Modal>
    </>
  );
};

export default CollectSampleModal;
